import { useMemo } from 'react'
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { useDailyBankBalances, useFacility, useForecastDrift } from './api'
import {
  CashflowV2Error,
  CashflowV2Loading,
  CashflowV2Unconfigured,
  looksUnconfigured,
} from './components/PageStates'
import { colors, fmt } from './utils'
import type { DailyBankBalance, FacilityConfig, ForecastDriftRow } from './types'

function addDaysIso(iso: string, deltaDays: number): string {
  const [year, month, day] = iso.split('-').map(Number)
  const date = new Date(Date.UTC(year, month - 1, day))
  date.setUTCDate(date.getUTCDate() + deltaDays)
  return date.toISOString().slice(0, 10)
}

function isHighOrCriticalSeverity(severity: string): boolean {
  const normalized = severity.toLowerCase()
  return normalized === 'high' || normalized === 'critical'
}

function KpiCard({
  label,
  value,
  subtext,
  color,
}: {
  label: string
  value: string
  subtext?: string
  color?: string
}) {
  return (
    <div className="rounded-xl bg-white shadow-sm border border-gray-100 p-5 flex flex-col gap-1 min-w-0 dark:bg-dark-surface dark:border-dark-border">
      <span className="text-xs font-medium uppercase tracking-wide text-gray-400 dark:text-gray-500">
        {label}
      </span>
      <span
        className="text-2xl font-semibold truncate text-gray-900 dark:text-gray-100"
        style={color ? { color } : undefined}
      >
        {value}
      </span>
      {subtext && (
        <span className="text-xs text-gray-400 dark:text-gray-500 truncate">
          {subtext}
        </span>
      )}
    </div>
  )
}

function severityBadgeClass(severity: string): string {
  const normalized = severity.toLowerCase()
  if (normalized === 'low') return 'bg-emerald-50 text-emerald-700'
  if (normalized === 'medium') return 'bg-amber-50 text-amber-700'
  if (normalized === 'high' || normalized === 'critical') {
    return 'bg-red-50 text-red-700'
  }
  return 'bg-gray-100 text-gray-600'
}

interface ChartDatum extends ForecastDriftRow {
  highlightArea: number | null
}

function DriftChartTooltip({
  active,
  payload,
}: {
  active?: boolean
  payload?: { payload: ChartDatum }[]
}) {
  if (!active || !payload?.[0]) return null
  const datum = payload[0].payload
  return (
    <div className="rounded-lg bg-gray-900 text-white text-xs px-3 py-2 shadow-lg space-y-1">
      <p className="font-semibold">{fmt.shortDate(datum.date)}</p>
      <p>Actual: {fmt.currency(datum.actual_bank_total)}</p>
      <p>Forecast: {fmt.currency(datum.forecast_ending_cash)}</p>
      <p>
        Drift: {fmt.currency(datum.drift_amount)} ({fmt.pct(datum.drift_pct)})
      </p>
      <p>Severity: {datum.severity}</p>
    </div>
  )
}

export default function DriftMonitorPage() {
  const driftQuery = useForecastDrift()
  const bankQuery = useDailyBankBalances()
  const facilityQuery = useFacility()

  const driftRows: ForecastDriftRow[] = useMemo(
    () => driftQuery.data ?? [],
    [driftQuery.data],
  )
  const bankRows: DailyBankBalance[] = useMemo(
    () => bankQuery.data ?? [],
    [bankQuery.data],
  )
  const facility: FacilityConfig | undefined = facilityQuery.data

  const latestDriftDate = driftRows[0]?.date

  const cutoff90 = latestDriftDate ? addDaysIso(latestDriftDate, -89) : null
  const cutoff30 = latestDriftDate ? addDaysIso(latestDriftDate, -29) : null
  const cutoff7 = latestDriftDate ? addDaysIso(latestDriftDate, -6) : null

  const driftLast90 = useMemo(() => {
    if (!cutoff90) return []
    return driftRows.filter(row => row.date >= cutoff90)
  }, [cutoff90, driftRows])

  const driftLast30 = useMemo(() => {
    if (!cutoff30) return []
    return driftRows.filter(row => row.date >= cutoff30)
  }, [cutoff30, driftRows])

  const driftLast7 = useMemo(() => {
    if (!cutoff7) return []
    return driftRows.filter(row => row.date >= cutoff7)
  }, [cutoff7, driftRows])

  const interventionAlert = useMemo(
    () =>
      driftLast30.some(
        row =>
          isHighOrCriticalSeverity(row.severity) && row.requires_intervention,
      ),
    [driftLast30],
  )

  const latestBankBalance = useMemo(() => {
    if (!bankRows.length) return null as DailyBankBalance | null
    return [...bankRows].sort((a, b) => b.date.localeCompare(a.date))[0]
  }, [bankRows])

  const forecastAccuracyPct = useMemo(() => {
    const latest = driftRows[0]
    if (!latest) return null
    const accuracy = 100 - Math.abs(latest.drift_pct)
    return Math.max(0, Math.min(100, accuracy))
  }, [driftRows])

  const criticalDriftDays7 = useMemo(
    () => driftLast7.filter(row => isHighOrCriticalSeverity(row.severity)).length,
    [driftLast7],
  )

  const interventionCount30 = useMemo(
    () =>
      driftLast30.filter(
        row =>
          isHighOrCriticalSeverity(row.severity) && row.requires_intervention,
      ).length,
    [driftLast30],
  )

  const chartDataAsc: ChartDatum[] = useMemo(() => {
    const sorted = [...driftLast90].sort((a, b) => a.date.localeCompare(b.date))
    return sorted.map(row => ({
      ...row,
      highlightArea: isHighOrCriticalSeverity(row.severity)
        ? Math.max(row.actual_bank_total, row.forecast_ending_cash)
        : null,
    }))
  }, [driftLast90])

  const tableRows = useMemo(() => {
    if (!cutoff30) return []
    return driftRows.filter(row => row.date >= cutoff30)
  }, [cutoff30, driftRows])

  const isLoading =
    driftQuery.isLoading || bankQuery.isLoading || facilityQuery.isLoading
  const firstError = driftQuery.error || bankQuery.error || facilityQuery.error

  if (isLoading) {
    return <CashflowV2Loading label="Loading drift monitor…" />
  }

  if (firstError) {
    if (looksUnconfigured(firstError)) {
      return <CashflowV2Unconfigured />
    }
    return (
      <CashflowV2Error
        title="Failed to load drift monitor data"
        description={(firstError as Error).message}
        onRetry={() => {
          driftQuery.refetch()
          bankQuery.refetch()
          facilityQuery.refetch()
        }}
      />
    )
  }

  return (
    <div className="space-y-6 pb-12">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Drift Monitor
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Forecast vs actual bank cash drift, severity, and intervention signals.
        </p>
      </div>

      {interventionAlert && (
        <div className="rounded-xl bg-red-600 border border-red-700 px-4 py-3 text-sm text-white shadow-sm">
          <p className="font-semibold">Intervention required</p>
          <p className="mt-1 text-red-50">
            One or more days in the last 30 show high or critical drift with an
            active intervention flag. Review the drift detail and driver notes
            below.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Current Bank Balance"
          value={
            latestBankBalance ? fmt.currency(latestBankBalance.available_balance) : '—'
          }
          subtext={latestBankBalance ? fmt.shortDate(latestBankBalance.date) : undefined}
          color={colors.status.green}
        />
        <KpiCard
          label="Forecast Accuracy"
          value={forecastAccuracyPct != null ? fmt.pct(forecastAccuracyPct) : '—'}
          subtext={driftRows[0] ? `As of ${fmt.shortDate(driftRows[0].date)}` : undefined}
          color={
            forecastAccuracyPct == null
              ? undefined
              : forecastAccuracyPct >= 95
                ? colors.status.green
                : forecastAccuracyPct >= 85
                  ? colors.status.yellow
                  : colors.status.red
          }
        />
        <KpiCard
          label="Critical Drift Days"
          value={fmt.num(criticalDriftDays7)}
          subtext="High / critical severity (last 7 days)"
          color={criticalDriftDays7 === 0 ? colors.status.green : colors.status.red}
        />
        <KpiCard
          label="Intervention Required"
          value={fmt.num(interventionCount30)}
          subtext="High / critical + flag (last 30 days)"
          color={interventionCount30 === 0 ? colors.status.green : colors.status.red}
        />
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border p-5">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">
          Forecast vs Actual
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-4">
          Last 90 days (through latest drift date)
        </p>
        {chartDataAsc.length === 0 ? (
          <p className="text-sm text-gray-400">No drift history in this window.</p>
        ) : (
          <ResponsiveContainer width="100%" height={360}>
            <ComposedChart data={chartDataAsc} margin={{ top: 8, right: 16, bottom: 0, left: 8 }}>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={colors.chart.gridLine}
                vertical={false}
              />
              <XAxis
                dataKey="date"
                tickFormatter={fmt.shortDate}
                tick={{ fontSize: 11, fill: colors.text.secondary }}
                minTickGap={32}
              />
              <YAxis
                tickFormatter={(value: number) => fmt.compact(value)}
                tick={{ fontSize: 11, fill: colors.text.secondary }}
                width={76}
              />
              <Tooltip content={<DriftChartTooltip />} cursor={{ strokeDasharray: '3 3' }} />
              <Area
                type="monotone"
                dataKey="highlightArea"
                name="Severity highlight"
                stroke="none"
                fill="rgba(239, 68, 68, 0.22)"
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="actual_bank_total"
                name="Actual bank total"
                stroke={colors.status.green}
                strokeWidth={2}
                dot={false}
                isAnimationActive={false}
              />
              <Line
                type="monotone"
                dataKey="forecast_ending_cash"
                name="Forecast ending cash"
                stroke={colors.accent.indigo}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                isAnimationActive={false}
              />
              {facility != null && (
                <ReferenceLine
                  y={facility.minimum_cash_covenant}
                  stroke={colors.status.red}
                  strokeDasharray="6 4"
                  strokeWidth={1.5}
                  label={{
                    value: 'Min covenant',
                    position: 'insideTopRight',
                    fill: colors.status.red,
                    fontSize: 11,
                  }}
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="rounded-xl bg-white shadow-sm border border-gray-100 dark:bg-dark-surface dark:border-dark-border overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-dark-border">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">
            Drift Detail
          </h2>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
            Last 30 days, most recent first
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-gray-50 dark:bg-dark-surface-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                <th className="px-4 py-3 whitespace-nowrap">Date</th>
                <th className="px-4 py-3 text-right">Forecast</th>
                <th className="px-4 py-3 text-right">Actual</th>
                <th className="px-4 py-3 text-right">Drift $</th>
                <th className="px-4 py-3 text-right">Drift %</th>
                <th className="px-4 py-3">Direction</th>
                <th className="px-4 py-3">Severity</th>
                <th className="px-4 py-3">Driver</th>
                <th className="px-4 py-3">Intervention</th>
              </tr>
            </thead>
            <tbody>
              {tableRows.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-8 text-center text-gray-400 dark:text-gray-500 text-sm"
                  >
                    No drift rows in this window
                  </td>
                </tr>
              )}
              {tableRows.map((row, index) => (
                <tr
                  key={`${row.date}-${index}`}
                  className={
                    index % 2 === 0
                      ? 'bg-white dark:bg-dark-surface'
                      : 'bg-gray-50/80 dark:bg-dark-surface-2/40'
                  }
                >
                  <td className="px-4 py-2.5 text-gray-800 dark:text-gray-100 whitespace-nowrap">
                    {fmt.shortDate(row.date)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {fmt.currency(row.forecast_ending_cash)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {fmt.currency(row.actual_bank_total)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-900 dark:text-gray-100">
                    {fmt.currency(row.drift_amount)}
                  </td>
                  <td className="px-4 py-2.5 text-right tabular-nums text-gray-700 dark:text-gray-200">
                    {fmt.pct(row.drift_pct)}
                  </td>
                  <td className="px-4 py-2.5 text-gray-700 dark:text-gray-200">
                    {row.direction}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${severityBadgeClass(
                        row.severity,
                      )}`}
                    >
                      {row.severity}
                    </span>
                  </td>
                  <td
                    className="px-4 py-2.5 text-gray-700 dark:text-gray-200 max-w-[220px] truncate"
                    title={row.primary_driver}
                  >
                    {row.primary_driver}
                  </td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
                        row.requires_intervention
                          ? 'bg-red-50 text-red-700'
                          : 'bg-gray-100 dark:bg-dark-surface-2 text-gray-600 dark:text-gray-300'
                      }`}
                      title={row.intervention_notes ?? undefined}
                    >
                      {row.requires_intervention ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
